import { ArticleSectionRelatedLinksContainer as ArticleSectionRelatedLinkList } from '#components/Article/Sections/ArticleSectionRelatedLinkList';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { ArticleData } from '#shared/types/article';
import { TrackEventDataType } from '#types/tracking';
import { Tracktor } from 'react-tracktor';

import styles from './ArticleFooter.module.scss';
import { ArticleModuleSocialMediaSharer } from './Modules/ArticleModuleSocialMediaSharer';
import { ArticleSectionComments } from './Sections/ArticleSectionComments';
import { ArticleSectionMap } from './Sections/ArticleSectionMap';
import { ArticleSectionTags } from './Sections/ArticleSectionTags';
import type { ArticleSectionTagsFragment } from './Sections/__generated__/ArticleSectionTags.generated';
import type { ArticleFooterFragment } from './__generated__/ArticleFooter.generated';

type Props = {
	article: ArticleData;
	bodyClassName?: string;
};

function LinkList({ links, className = '' }: { className?: string; links: ArticleFooterFragment['links'] }) {
	if (!links || !Array.isArray(links)) {
		return null;
	}

	return <ArticleSectionRelatedLinkList className={className} links={links} />;
}

function MobileTagsSection({ tags, className = '' }: { className?: string; tags: ArticleSectionTagsFragment[] }) {
	return (
		<section className={`${styles.tagsSection} ${className}`}>
			<h2 className={styles.tagsTitle}>Schlagwörter</h2>
			<ArticleSectionTags tags={tags} />
		</section>
	);
}

export function ArticleFooter({ article, bodyClassName = '' }: Props) {
	const articleRelatedLinks = (article.links || []).filter(notNullOrUndefined);
	const articleTags = article.tags2.nodes.filter(notNullOrUndefined);

	return (
		<footer className={`${styles.section} ${article.geolocation ? styles.map : ''}`}>
			<ArticleModuleSocialMediaSharer article={article} />

			<LinkList links={articleRelatedLinks} className={bodyClassName} />

			{articleTags.length > 0 && <MobileTagsSection tags={articleTags} className={bodyClassName} />}

			<Tracktor
				// The key ensure that the component doesn't get reused for a different article and
				//  accidentally report the next article as read when routing to it.
				key={article.rowId}
				eventData={{ data: { type: TrackEventDataType.ArticleRead }, event: { type: 'PublisherImpression' } }}
			>
				{({ intersectionRef }) => <span ref={intersectionRef} />}
			</Tracktor>

			<ArticleSectionComments article={article} className={bodyClassName} />

			{article.geolocation ? <ArticleSectionMap geolocation={article.geolocation} /> : null}
		</footer>
	);
}
